import { Link } from "react-router-dom";
import { useState } from "react";
import { AppShellV8 } from "../AppShellV8";

interface Agent {
  role: string;
  desc: string;
  status: "active" | "idle" | "paused";
  parent?: string;
  recentTasks: { task: string; status: "done" | "failed" | "in-progress"; time: string }[];
}

const AGENTS: Record<string, Agent> = {
  ceo: { role: "CEO Agent", desc: "Orchestrates all agent activity, delegates tasks, monitors quality", status: "active", recentTasks: [
    { task: "Delegated MDS campaign tasks", status: "done", time: "2h ago" },
    { task: "Quality review on TR social calendar", status: "in-progress", time: "Now" },
  ]},
  cmo: { role: "CMO Agent", desc: "Marketing strategy, campaign planning, content direction", status: "active", parent: "ceo", recentTasks: [
    { task: "MDS deposit campaign strategy", status: "done", time: "1h ago" },
  ]},
  cto: { role: "CTO Agent", desc: "Technical integrations, data analysis, system health", status: "idle", parent: "ceo", recentTasks: [
    { task: "Synced Google Calendar events", status: "done", time: "3h ago" },
  ]},
  copywriter: { role: "Copywriter", desc: "Email sequences, landing pages, ad copy, social posts", status: "active", parent: "cmo", recentTasks: [
    { task: "Generated MDS email sequence (3 emails)", status: "done", time: "45m ago" },
    { task: "Generating TR social calendar posts", status: "in-progress", time: "Now" },
    { task: "FB welcome email — tone mismatch", status: "failed", time: "2h ago" },
  ]},
  researcher: { role: "Researcher", desc: "Competitive analysis, market data, industry benchmarks", status: "idle", parent: "cmo", recentTasks: [
    { task: "Compiled VBC competitive landscape — 12 data points", status: "done", time: "4h ago" },
    { task: "Updated FB industry benchmarks", status: "done", time: "Yesterday" },
  ]},
  strategist: { role: "Strategist", desc: "Campaign frameworks, audience segmentation, positioning", status: "idle", parent: "cmo", recentTasks: [
    { task: "Analyzed TR market position", status: "done", time: "5h ago" },
  ]},
  analyst: { role: "Data Analyst", desc: "Performance metrics, ROI tracking, reporting", status: "idle", parent: "cto", recentTasks: [
    { task: "EG monthly performance report", status: "done", time: "Yesterday" },
  ]},
  integrations: { role: "Integration Specialist", desc: "External tool syncing, data pipeline management", status: "idle", parent: "cto", recentTasks: [
    { task: "Zoom transcript auto-import configured", status: "done", time: "2 days ago" },
  ]},
};

interface ActivityItem {
  agent: string;
  agentColor: string;
  text: string;
  status: "done" | "failed" | "in-progress";
  time: string;
  date: string;
  failReason?: string;
}

const ACTIVITY: ActivityItem[] = [
  { agent: "Copywriter", agentColor: "bg-blue-500", text: "Generated 3 email drafts for MDS Deposit Campaign", status: "done", time: "45 min ago", date: "Today" },
  { agent: "CEO Agent", agentColor: "bg-gray-700", text: "Quality review on TR social calendar — checking brand alignment", status: "in-progress", time: "12 min ago", date: "Today" },
  { agent: "Copywriter", agentColor: "bg-blue-500", text: "Generating Timber Ridge social calendar — 14 posts across 3 platforms", status: "in-progress", time: "Now", date: "Today" },
  { agent: "Copywriter", agentColor: "bg-blue-500", text: "FB welcome email — tone mismatch with voice profile, regenerating", status: "failed", time: "2h ago", date: "Today", failReason: "Output tone scored 0.61 against voice profile (min 0.80). Auto-retry queued." },
  { agent: "Researcher", agentColor: "bg-emerald-500", text: "Compiled VBC competitive landscape — 12 data points added to KB", status: "done", time: "4h ago", date: "Today" },
  { agent: "Strategist", agentColor: "bg-purple-500", text: "Analyzed TR market position — updated competitive positioning doc", status: "done", time: "5h ago", date: "Today" },
  { agent: "Data Analyst", agentColor: "bg-orange-500", text: "ROI pull for MDS dashboard — analytics API timed out", status: "failed", time: "5h ago", date: "Today", failReason: "Google Analytics API returned 504 after 30s. Needs reconnect or manual retry." },
  { agent: "CTO Agent", agentColor: "bg-teal-500", text: "Synced 12 Google Calendar events for this week", status: "done", time: "3h ago", date: "Today" },
  { agent: "CMO Agent", agentColor: "bg-amber-500", text: "Planned MDS deposit campaign strategy — 3 phases, 12 assets", status: "done", time: "1h ago", date: "Today" },
  { agent: "Integration Specialist", agentColor: "bg-cyan-500", text: "CRM contact sync to VBC HubSpot — auth token expired", status: "failed", time: "6h ago", date: "Today", failReason: "HubSpot OAuth token expired. Re-authorize the integration to resume sync." },
  { agent: "Data Analyst", agentColor: "bg-orange-500", text: "Generated EG monthly performance report — emailed to stakeholders", status: "done", time: "Yesterday", date: "Yesterday" },
  { agent: "Integration Specialist", agentColor: "bg-cyan-500", text: "Configured Zoom transcript auto-import for all team accounts", status: "done", time: "2 days ago", date: "May 25" },
  { agent: "Researcher", agentColor: "bg-emerald-500", text: "Updated FB industry benchmarks from latest FDIC data", status: "done", time: "Yesterday", date: "Yesterday" },
  { agent: "Copywriter", agentColor: "bg-blue-500", text: "Processed VBC meeting transcript → generated brand voice analysis", status: "done", time: "Yesterday", date: "Yesterday" },
];

const STATUS_BADGE: Record<string, string> = {
  done: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/10",
  failed: "bg-red-50 text-red-700 ring-1 ring-red-600/10",
  "in-progress": "bg-blue-50 text-blue-700 ring-1 ring-blue-600/10",
};

const NODE_STATUS: Record<string, string> = { active: "bg-green-500", idle: "bg-gray-300", paused: "bg-amber-400" };

const BORDER_COLOR: Record<string, string> = {
  done: "border-l-emerald-400",
  failed: "border-l-red-400",
  "in-progress": "border-l-blue-400",
};

const SPARKLINE = [4, 7, 5, 8, 6, 9, 7];

// Default-select the currently-running agent (the live job) so the detail
// panel is populated on arrival; fall back to the CEO Agent. Only a genuinely
// zero-agent account should ever see the empty state.
const DEFAULT_AGENT =
  Object.keys(AGENTS).find(k =>
    AGENTS[k].recentTasks.some(t => t.status === "in-progress")
  ) ??
  (Object.keys(AGENTS).includes("ceo") ? "ceo" : Object.keys(AGENTS)[0] ?? null);

const hasAgents = Object.keys(AGENTS).length > 0;

export function AgentActivity() {
  const [showOrgChart, setShowOrgChart] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState<string | null>(
    hasAgents ? DEFAULT_AGENT : null
  );
  const [agentStates, setAgentStates] = useState<Record<string, string>>(
    Object.fromEntries(Object.entries(AGENTS).map(([k, v]) => [k, v.status]))
  );
  const [showFailures, setShowFailures] = useState(false);

  const failures = ACTIVITY.filter(a => a.status === "failed");

  // Selecting a node: clicking the already-selected node falls back to the
  // default agent (rather than emptying the panel) so the detail pane stays
  // populated whenever any agent exists. The empty state is reserved for a
  // genuinely zero-agent account.
  const selectAgent = (k: string) =>
    setSelectedAgent(prev => (prev === k ? DEFAULT_AGENT : k));

  const togglePause = (key: string) => {
    setAgentStates(prev => ({ ...prev, [key]: prev[key] === "paused" ? "idle" : "paused" }));
  };

  const agentActivity = (agentRole: string) =>
    ACTIVITY.filter(a => a.agent === agentRole);

  return (
    <AppShellV8 activeApp="agent-activity" breadcrumb={["Agent Activity"]} reviewCount={7}>
      <div className="flex h-full overflow-hidden">
        {/* -- Left column: org chart + activity feed -- */}
        <div className="flex-1 overflow-y-auto border-r border-black/[0.06]">
          {/* Org Chart */}
          <div className="border-b border-black/[0.06]">
            <button
              onClick={() => setShowOrgChart(!showOrgChart)}
              className="flex w-full items-center justify-between px-6 py-3.5 transition-all duration-150 hover:bg-gray-50/80"
            >
              <h2 className="text-[13px] font-semibold text-gray-900">Agent Org Chart</h2>
              <svg
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${showOrgChart ? "rotate-180" : "rotate-0"}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
              </svg>
            </button>
            <div
              className={`grid transition-all duration-200 ease-in-out ${
                showOrgChart ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
              }`}
            >
              <div className="overflow-hidden">
                <div className="px-6 pb-6">
                  <div className="flex flex-col items-center">
                    {/* CEO (root) */}
                    <AgentNode k="ceo" agent={AGENTS.ceo} state={agentStates.ceo} onToggle={() => togglePause("ceo")} selected={selectedAgent === "ceo"} onSelect={() => selectAgent("ceo")} />

                    {/* CEO → reports: trunk + horizontal bus + two drops */}
                    <div className="h-4 w-px bg-gray-300" />
                    <div className="relative flex w-full max-w-[760px] justify-center gap-12">
                      {/* horizontal bus connecting the two branch trunks */}
                      <div className="pointer-events-none absolute left-1/4 right-1/4 top-0 h-px bg-gray-300" />

                      {/* CMO branch */}
                      <div className="relative flex flex-col items-center">
                        <div className="h-4 w-px bg-gray-300" />
                        <AgentNode k="cmo" agent={AGENTS.cmo} state={agentStates.cmo} onToggle={() => togglePause("cmo")} selected={selectedAgent === "cmo"} onSelect={() => selectAgent("cmo")} />
                        <div className="h-4 w-px bg-gray-300" />
                        <Connectors n={3} />
                        <div className="flex gap-3">
                          {["copywriter", "researcher", "strategist"].map(k => (
                            <div key={k} className="flex flex-col items-center">
                              <div className="h-4 w-px bg-gray-300" />
                              <AgentNode k={k} agent={AGENTS[k]} state={agentStates[k]} onToggle={() => togglePause(k)} selected={selectedAgent === k} onSelect={() => selectAgent(k)} />
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* CTO branch */}
                      <div className="relative flex flex-col items-center">
                        <div className="h-4 w-px bg-gray-300" />
                        <AgentNode k="cto" agent={AGENTS.cto} state={agentStates.cto} onToggle={() => togglePause("cto")} selected={selectedAgent === "cto"} onSelect={() => selectAgent("cto")} />
                        <div className="h-4 w-px bg-gray-300" />
                        <Connectors n={2} />
                        <div className="flex gap-3">
                          {["analyst", "integrations"].map(k => (
                            <div key={k} className="flex flex-col items-center">
                              <div className="h-4 w-px bg-gray-300" />
                              <AgentNode k={k} agent={AGENTS[k]} state={agentStates[k]} onToggle={() => togglePause(k)} selected={selectedAgent === k} onSelect={() => selectAgent(k)} />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Activity Feed */}
          <div className="p-6">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="text-[13px] font-semibold text-gray-900">Activity Feed</h2>
              {failures.length > 0 && (
                <button
                  onClick={() => setShowFailures(v => !v)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium transition-all duration-150 active:scale-[0.97] ${
                    showFailures
                      ? "bg-red-600 text-white shadow-sm hover:bg-red-700"
                      : "bg-red-50 text-red-700 ring-1 ring-red-600/15 hover:bg-red-100"
                  }`}
                >
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0 3.75h.008M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {showFailures ? "Showing failures" : `${failures.length} failed`}
                </button>
              )}
            </div>

            {/* Recent failures panel */}
            {showFailures && failures.length > 0 && (
              <div className="mb-4 rounded-lg border border-red-200/70 bg-red-50/40 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-[11px] font-semibold uppercase tracking-wider text-red-700">
                    Recent failures
                  </h3>
                  <button
                    onClick={() => setShowFailures(false)}
                    className="rounded p-0.5 text-red-400 transition-all duration-150 hover:bg-red-100 hover:text-red-600"
                    aria-label="Close failures"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="space-y-2">
                  {failures.map((f, i) => (
                    <a
                      key={i}
                      href={`#failure-${i}`}
                      className="block rounded-md border border-red-200/60 bg-white p-2.5 shadow-sm transition-all duration-150 hover:border-red-300 hover:shadow-md"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[12px] font-semibold text-gray-800">{f.agent}</span>
                        <span className="shrink-0 text-[10px] text-gray-600">{f.time}</span>
                      </div>
                      <p className="mt-0.5 text-[12px] text-gray-700">{f.text}</p>
                      {f.failReason && (
                        <p className="mt-1 text-[11px] leading-snug text-red-700">{f.failReason}</p>
                      )}
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              {ACTIVITY.map((a, i) => {
                const showDate = i === 0 || ACTIVITY[i - 1].date !== a.date;
                const failIndex = failures.indexOf(a);
                return (
                  <div key={i}>
                    {showDate && (
                      <p className="mb-2.5 mt-5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 first:mt-0">
                        {a.date}
                      </p>
                    )}
                    <div
                      id={a.status === "failed" && failIndex >= 0 ? `failure-${failIndex}` : undefined}
                      className={`flex items-start gap-3 rounded-lg border border-black/[0.06] border-l-2 ${BORDER_COLOR[a.status]} bg-white p-3 shadow-sm transition-all duration-150 hover:shadow-md scroll-mt-4`}
                    >
                      <span
                        className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${a.agentColor} text-[8px] font-bold text-white shadow-sm`}
                      >
                        {a.agent.charAt(0)}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-600">
                          <span className="font-semibold text-gray-700">{a.agent}</span>
                          <span className="mx-1.5 text-gray-300">&middot;</span>
                          {a.time}
                        </p>
                        <p className="mt-0.5 text-[13px] text-gray-700">{a.text}</p>
                        {a.status === "failed" && a.failReason && (
                          <p className="mt-1 text-[11px] leading-snug text-red-600">{a.failReason}</p>
                        )}
                        {a.status === "in-progress" && (
                          <RunControls className="mt-2" size="sm" />
                        )}
                      </div>
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_BADGE[a.status]}`}
                      >
                        {a.status === "in-progress" ? "In Progress" : a.status === "done" ? "Done" : "Failed"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* -- Right column: agent detail + status cards -- */}
        <div className="w-[340px] shrink-0 overflow-y-auto border-l border-black/[0.06] p-4 space-y-4">
          {/* Agent Detail Panel */}
          {selectedAgent && AGENTS[selectedAgent] && (() => {
            const agent = AGENTS[selectedAgent];
            const tasks = agentActivity(agent.role);
            const agentState = agentStates[selectedAgent];
            return (
              <div className="rounded-xl border border-blue-200/60 bg-white shadow-sm ring-1 ring-blue-500/5">
                <div className="border-b border-blue-100/60 px-4 py-3.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <span
                        className={`h-2.5 w-2.5 rounded-full ${NODE_STATUS[agentState] || "bg-gray-300"} ${
                          agentState === "active" ? "animate-pulse" : ""
                        } ring-2 ring-white`}
                      />
                      <h3 className="text-[15px] font-semibold text-gray-900">{agent.role}</h3>
                    </div>
                    <button
                      onClick={() => setSelectedAgent(DEFAULT_AGENT)}
                      title="Back to default agent"
                      className="rounded-md p-1 text-gray-400 transition-all duration-150 hover:bg-gray-100 hover:text-gray-600"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <p className="mt-1.5 text-xs text-gray-600 leading-relaxed">{agent.desc}</p>
                  <div className="mt-2.5 flex items-center gap-2.5">
                    <span
                      className={`rounded-full px-2.5 py-0.5 text-[10px] font-medium ring-1 ${
                        agentState === "active"
                          ? "bg-emerald-50 text-emerald-700 ring-emerald-600/10"
                          : agentState === "paused"
                          ? "bg-amber-50 text-amber-700 ring-amber-600/10"
                          : "bg-gray-50 text-gray-600 ring-gray-900/5"
                      }`}
                    >
                      {agentState === "active" ? "Active" : agentState === "paused" ? "Paused" : "Idle"}
                    </span>
                    {agent.parent && (
                      <span className="text-[10px] text-gray-500">
                        Reports to {AGENTS[agent.parent]?.role}
                      </span>
                    )}
                  </div>
                </div>

                <div className="px-4 py-3.5">
                  <h4 className="mb-2.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                    Recent Tasks
                  </h4>
                  {agent.recentTasks.length > 0 ? (
                    <div className="space-y-2.5">
                      {agent.recentTasks.map((t, i) => (
                        <div key={i} className="flex items-start gap-2.5">
                          <span
                            className={`mt-1 h-2 w-2 shrink-0 rounded-full ${
                              t.status === "done"
                                ? "bg-emerald-500"
                                : t.status === "failed"
                                ? "bg-red-500"
                                : "bg-amber-500 animate-pulse"
                            }`}
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-[13px] text-gray-700">{t.task}</p>
                            <p className="text-[10px] text-gray-500">
                              <span
                                className={`font-medium ${
                                  t.status === "done"
                                    ? "text-emerald-600"
                                    : t.status === "failed"
                                    ? "text-red-600"
                                    : "text-amber-600"
                                }`}
                              >
                                {t.status === "done" ? "Done" : t.status === "failed" ? "Failed" : "Running"}
                              </span>
                              <span className="mx-1 text-gray-300">&middot;</span>
                              {t.time}
                            </p>
                            {t.status === "in-progress" && (
                              <RunControls className="mt-1.5" size="sm" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">No recent tasks</p>
                  )}
                </div>

                {tasks.length > 0 && (
                  <div className="border-t border-black/[0.06] px-4 py-3.5">
                    <h4 className="mb-2.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                      Activity Feed
                    </h4>
                    <div className="space-y-2">
                      {tasks.slice(0, 5).map((a, i) => (
                        <div key={i} className="flex items-center justify-between gap-2">
                          <p className="truncate text-[12px] text-gray-600">{a.text.substring(0, 45)}...</p>
                          <span
                            className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-medium ${STATUS_BADGE[a.status]}`}
                          >
                            {a.status === "in-progress" ? "Active" : a.status === "done" ? "Done" : "Failed"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="border-t border-black/[0.06] px-4 py-3.5">
                  <h4 className="mb-2.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                    Agent Health
                  </h4>
                  <div className="mb-2 flex items-end gap-1 h-10">
                    <svg viewBox="0 0 140 40" className="h-10 w-full" preserveAspectRatio="none">
                      <polyline
                        fill="none"
                        stroke="#93c5fd"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        points={SPARKLINE.map((v, i) => `${i * (140 / (SPARKLINE.length - 1))},${40 - (v / 10) * 40}`).join(" ")}
                      />
                      <polyline
                        fill="url(#sparkGrad)"
                        stroke="none"
                        points={`0,40 ${SPARKLINE.map((v, i) => `${i * (140 / (SPARKLINE.length - 1))},${40 - (v / 10) * 40}`).join(" ")} 140,40`}
                      />
                      <defs>
                        <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#93c5fd" stopOpacity="0.3" />
                          <stop offset="100%" stopColor="#93c5fd" stopOpacity="0.02" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Success rate</span>
                      <span className="text-xs font-semibold text-emerald-600">
                        {agentState === "active" ? "96.1%" : "94.2%"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Runs (7d)</span>
                      <span className="text-xs font-semibold text-gray-700">
                        {agentState === "active" ? "48" : "23"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Avg duration</span>
                      <span className="text-xs font-semibold text-gray-700">
                        {agentState === "active" ? "3m 12s" : "1m 45s"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Empty state — reserved for a genuinely zero-agent account.
              When agents exist, the detail panel is always populated (default-selected). */}
          {(!hasAgents || !selectedAgent) && (
            <div className="rounded-xl border border-dashed border-gray-200/80 bg-gray-50/50 px-4 py-10 text-center">
              <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
                  />
                </svg>
              </div>
              <p className="text-[13px] font-medium text-gray-600">No agents yet</p>
              <p className="mt-1 text-xs text-gray-500">
                Agents will appear here once your workspace is set up
              </p>
            </div>
          )}

          {/* Live Status */}
          <div className="rounded-xl border border-black/[0.06] bg-white p-4 shadow-sm ring-1 ring-black/[0.04]">
            <div className="mb-3 flex items-center gap-2">
              <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-green-500 ring-2 ring-green-500/20" />
              <h3 className="text-[13px] font-semibold text-gray-900">Live Status</h3>
            </div>
            <p className="text-[13px] font-medium text-gray-700">
              Generating Timber Ridge social calendar...
            </p>
            <div className="mt-2.5 flex items-center gap-2 text-xs text-gray-600">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 text-[7px] font-bold text-white shadow-sm">
                C
              </span>
              <span>Copywriter</span>
              <span className="ml-auto font-mono text-[11px]">2m 34s</span>
            </div>
            <div className="mt-2.5 h-1.5 rounded-full bg-gray-100">
              <div className="h-1.5 w-2/3 animate-pulse rounded-full bg-blue-500 transition-all duration-150" />
            </div>
            <RunControls className="mt-3" size="md" />
          </div>

          {/* Cost / Budget */}
          <div className="rounded-xl border border-black/[0.06] bg-white p-4 shadow-sm ring-1 ring-black/[0.04]">
            <h3 className="mb-3 text-[13px] font-semibold text-gray-900">Cost / Budget</h3>
            <div className="mb-1.5 flex items-baseline justify-between">
              <span className="text-xl font-bold tracking-tight text-gray-900">$847</span>
              <span className="text-xs text-gray-600">of $2,000</span>
            </div>
            <div className="mb-2 h-2 rounded-full bg-gray-100">
              <div
                className="h-2 rounded-full bg-blue-500 transition-all duration-300"
                style={{ width: "42%" }}
              />
            </div>
            <div className="flex items-center justify-between text-xs text-gray-600">
              <span>42% used</span>
              <span>~$28/day avg</span>
            </div>
            <div className="mt-2 flex items-center gap-1.5 text-xs">
              <span className="font-medium text-amber-600">+12%</span>
              <span className="text-gray-600">vs last month</span>
            </div>
            <button className="mt-3 w-full rounded-lg border border-black/[0.06] py-1.5 text-xs font-medium text-gray-600 transition-all duration-150 hover:bg-gray-50 hover:border-black/10">
              Set Budget
            </button>
          </div>

          {/* System Health */}
          <div className="rounded-xl border border-black/[0.06] bg-white p-4 shadow-sm ring-1 ring-black/[0.04]">
            <h3 className="mb-3 text-[13px] font-semibold text-gray-900">System Health</h3>
            <div className="mb-2 flex items-end gap-1 h-12">
              <svg viewBox="0 0 140 48" className="h-12 w-full" preserveAspectRatio="none">
                <polyline
                  fill="none"
                  stroke="#93c5fd"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  points={SPARKLINE.map((v, i) => `${i * (140 / (SPARKLINE.length - 1))},${48 - (v / 10) * 48}`).join(" ")}
                />
                <polyline
                  fill="url(#sysGrad)"
                  stroke="none"
                  points={`0,48 ${SPARKLINE.map((v, i) => `${i * (140 / (SPARKLINE.length - 1))},${48 - (v / 10) * 48}`).join(" ")} 140,48`}
                />
                <defs>
                  <linearGradient id="sysGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#93c5fd" stopOpacity="0.25" />
                    <stop offset="100%" stopColor="#93c5fd" stopOpacity="0.02" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <p className="text-[10px] text-gray-500 mb-3">7-day activity across all agents</p>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">Success rate</span>
                <span className="text-xs font-semibold text-emerald-600">94.2%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">Total runs</span>
                <span className="text-xs font-semibold text-gray-700">342</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">Failed</span>
                <button
                  onClick={() => {
                    setShowFailures(true);
                    if (typeof document !== "undefined") {
                      requestAnimationFrame(() =>
                        document.getElementById("failure-0")?.scrollIntoView({ behavior: "smooth", block: "center" })
                      );
                    }
                  }}
                  className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-semibold text-red-600 ring-1 ring-red-600/15 transition-all duration-150 hover:bg-red-50 active:scale-[0.97]"
                >
                  {failures.length}
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppShellV8>
  );
}

// Horizontal connector bus that spans from the center of the first child node
// to the center of the last child node. Children are w-[164px] with gap-3 (12px),
// so each column is 176px wide; the bus is inset by half a node (82px) on each side.
function Connectors({ n }: { n: number }) {
  const colW = 164 + 12; // node width + gap
  const totalW = n * 164 + (n - 1) * 12;
  const inset = 82; // half a node, so the bus reaches each node's center
  return (
    <div className="relative h-px" style={{ width: totalW }}>
      <div
        className="absolute top-0 h-px bg-gray-300"
        style={{ left: inset, right: inset }}
      />
      {Array.from({ length: n }).map((_, i) => (
        <div
          key={i}
          className="absolute top-0 h-px w-px bg-gray-300"
          style={{ left: i * colW + 82 }}
        />
      ))}
    </div>
  );
}

// Run-level controls for a live/in-progress job — Pause, Stop, Flag.
// Prototype (non-functional) but styled as real controls with hover/active states.
function RunControls({ className = "", size = "md" }: { className?: string; size?: "sm" | "md" }) {
  const iconSize = size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5";
  const pad = size === "sm" ? "px-1.5 py-1" : "px-2 py-1.5";
  const gap = size === "sm" ? "gap-1.5" : "gap-2";
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  return (
    <div className={`flex items-center ${gap} ${className}`} onClick={stop}>
      <button
        type="button"
        title="Pause run"
        className={`inline-flex items-center gap-1 rounded-md ${pad} text-[11px] font-medium text-amber-700 bg-amber-50 ring-1 ring-amber-600/15 transition-all duration-150 hover:bg-amber-100 active:scale-[0.97]`}
      >
        <svg className={iconSize} viewBox="0 0 24 24" fill="currentColor">
          <rect x="6" y="5" width="4" height="14" rx="1" />
          <rect x="14" y="5" width="4" height="14" rx="1" />
        </svg>
        Pause
      </button>
      <button
        type="button"
        title="Stop run"
        className={`inline-flex items-center gap-1 rounded-md ${pad} text-[11px] font-medium text-red-700 bg-red-50 ring-1 ring-red-600/15 transition-all duration-150 hover:bg-red-100 active:scale-[0.97]`}
      >
        <svg className={iconSize} viewBox="0 0 24 24" fill="currentColor">
          <rect x="6" y="6" width="12" height="12" rx="2" />
        </svg>
        Stop
      </button>
      <button
        type="button"
        title="Flag for review"
        className={`inline-flex items-center gap-1 rounded-md ${pad} text-[11px] font-medium text-gray-600 bg-gray-50 ring-1 ring-gray-900/10 transition-all duration-150 hover:bg-gray-100 active:scale-[0.97]`}
      >
        <svg className={iconSize} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v18M3 4h13l-2 4 2 4H3" />
        </svg>
        Flag
      </button>
    </div>
  );
}

function AgentNode({
  k,
  agent,
  state,
  onToggle,
  selected,
  onSelect,
}: {
  k: string;
  agent: Agent;
  state: string;
  onToggle: () => void;
  selected: boolean;
  onSelect: () => void;
}) {
  const statusLabel = state === "active" ? "Active" : state === "paused" ? "Paused" : "Idle";
  const statusTextColor =
    state === "active" ? "text-green-700" : state === "paused" ? "text-amber-700" : "text-gray-500";
  return (
    <div
      onClick={onSelect}
      className={`w-[164px] cursor-pointer rounded-xl p-3 text-center transition-all duration-150 ${
        selected
          ? "bg-blue-50/40 shadow-md ring-2 ring-blue-500"
          : "bg-white shadow-sm ring-1 ring-black/[0.04] hover:ring-blue-300 hover:bg-blue-50/30 hover:shadow-md"
      }`}
    >
      <div className="mb-1 flex items-center justify-center gap-1.5">
        <span
          className={`h-2 w-2 rounded-full ${NODE_STATUS[state] || "bg-gray-300"} ${
            state === "active" ? "animate-pulse" : ""
          }`}
        />
        <span className="text-[13px] font-medium text-gray-900">{agent.role}</span>
      </div>
      {/* Status text label paired with the dot (not color-only) */}
      <div className="mb-1.5 flex items-center justify-center gap-1.5">
        <span className={`text-[10px] font-semibold uppercase tracking-wide ${statusTextColor}`}>
          {statusLabel}
        </span>
      </div>
      <p className="mb-2.5 text-xs text-gray-600 leading-relaxed">{agent.desc}</p>
      <div className="flex items-center justify-center gap-1.5">
        <Link
          to="/agents/knowledge-base"
          title="Edit agent"
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-medium text-blue-700 bg-blue-50 ring-1 ring-blue-600/15 transition-all duration-150 hover:bg-blue-100 active:scale-[0.97]"
          onClick={e => e.stopPropagation()}
        >
          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
          </svg>
          Edit
        </Link>
        <button
          type="button"
          title={state === "paused" ? "Resume agent" : "Pause agent"}
          onClick={e => {
            e.stopPropagation();
            onToggle();
          }}
          className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-medium ring-1 transition-all duration-150 active:scale-[0.97] ${
            state === "paused"
              ? "bg-green-50 text-green-700 ring-green-600/15 hover:bg-green-100"
              : "bg-amber-50 text-amber-700 ring-amber-600/15 hover:bg-amber-100"
          }`}
        >
          {state === "paused" ? (
            <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          ) : (
            <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="5" width="4" height="14" rx="1" />
              <rect x="14" y="5" width="4" height="14" rx="1" />
            </svg>
          )}
          {state === "paused" ? "Resume" : "Pause"}
        </button>
      </div>
    </div>
  );
}
